// src/pages/LoginPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  IconButton,
  Paper,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { login, getMe } from "../controllers/auth.controller";
import calmWaves from "../assets/images/calm-waves.png";

// ✅ Light theme (light-blue)
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2196f3" },
    background: { default: "#f6f9fc" },
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily: ["Vazirmatn", "IRANSans", "Roboto", "Arial"].join(","),
  },
});

export default function LoginPage({ setUser }) {
  const navigate = useNavigate();

  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    severity: "info", // success | error | warning | info
    message: "",
  });

  const canSubmit = useMemo(() => {
    return emailOrPhone.trim().length > 0 && password.trim().length >= 3 && !loading;
  }, [emailOrPhone, password, loading]);

  const closeToast = () => setToast((p) => ({ ...p, open: false }));

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeToast();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleLogin = async () => {
    if (!canSubmit) return;

    setLoading(true);
    setToast({ open: true, severity: "info", message: "در حال ورود..." });

    try {
      const data = await login(emailOrPhone, password, null);
      localStorage.setItem("token", data.token);

      const me = await getMe();
      localStorage.setItem("user", JSON.stringify(me));
      setUser?.(me);

      setToast({ open: true, severity: "success", message: "ورود موفقیت‌آمیز بود ✅" });

      setTimeout(() => {
        closeToast();
        navigate("/", { replace: true });
      }, 800);
    } catch (err) {
      const msg = err?.message || "خطا در ورود";
      setToast({ open: true, severity: "error", message: msg });
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* ✅ Page wrapper */}
      <Box
        dir="rtl"
        sx={{
          minHeight: "100vh",
          display: "flex",
          bgcolor: "background.default",
        }}
      >
        {/* ✅ Left panel (Desktop only) */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flex: 1,
            position: "relative",
            overflow: "hidden",
            alignItems: "center",
            justifyContent: "center",
            px: 6,
            background:
              "linear-gradient(135deg, rgba(33,150,243,0.18), rgba(33,150,243,0.05))",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${calmWaves})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "saturate(1.05)",
              opacity: 0.95,
            }}
          />

          {/* overlay soft */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(246,249,252,0.35), rgba(246,249,252,0.75))",
            }}
          />

          {/* text on image */}
          <Box sx={{ position: "relative", maxWidth: 520 }}>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{ color: "primary.main", mb: 1.2 }}
            >
              سامانه مدیریت بیماران اوتیسم
            </Typography>
            <Typography sx={{ color: "text.secondary", lineHeight: 1.9 }}>
              محیطی آرام، ساده و حرفه‌ای برای مدیریت بیماران، فرم‌ها و ارزیابی‌ها.
              <br />
              لطفاً با اطلاعات حساب خود وارد شوید.
            </Typography>
          </Box>
        </Box>

        {/* ✅ Right panel: Form (Desktop) + Full width (Mobile) */}
        <Box
          sx={{
            width: { xs: "100%", md: 520 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2, sm: 3 },
            py: { xs: 3, sm: 5 },
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: "100%",
              maxWidth: 520,
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            {/* ✅ Mobile header image */}
            <Box
              sx={{
                display: { xs: "block", md: "none" },
                height: 170,
                position: "relative",
                background:
                  "linear-gradient(135deg, rgba(33,150,243,0.18), rgba(33,150,243,0.06))",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url(${calmWaves})`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  opacity: 0.95,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(246,249,252,0.25), rgba(246,249,252,0.85))",
                }}
              />
            </Box>

            {/* ✅ Form content */}
            <Box
              sx={{
                p: { xs: 2.5, sm: 4 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar sx={{ bgcolor: "primary.main", width: 46, height: 46, mb: 1 }}>
                <LockOutlinedIcon />
              </Avatar>

              <Typography variant="h6" fontWeight={900} sx={{ mb: 0.5 }}>
                ورود به پنل کاریری
              </Typography>

              <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
                لطفاً ایمیل و رمز عبور را وارد کنید
              </Typography>

              <Box sx={{ width: "100%" }}>
                <TextField
                  fullWidth
                  label="ایمیل"
                  value={emailOrPhone}
                  onChange={(e) => setEmailOrPhone(e.target.value)}
                  margin="normal"
                  autoComplete="username"
                />

                <TextField
                  fullWidth
                  label="رمز عبور"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  autoComplete="current-password"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleLogin();
                  }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 2, py: 1.25, fontWeight: 900, borderRadius: 3 }}
                  disabled={!canSubmit}
                  onClick={handleLogin}
                >
                  {loading ? "در حال ورود..." : "ورود"}
                </Button>

                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 2, color: "text.secondary", lineHeight: 1.8 }}
                >
                 
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* ✅ Backdrop Toast (Closable) */}
        <Backdrop
          open={toast.open}
          onClick={closeToast}
          sx={{
            zIndex: (t) => t.zIndex.drawer + 999,
            color: "#fff",
            flexDirection: "column",
            p: 2,
          }}
        >
          {toast.severity === "info" && loading && <CircularProgress color="inherit" />}

          <Box
            sx={{
              mt: 2,
              width: "min(520px, 92vw)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              onClick={closeToast}
              sx={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>

            <Alert
              severity={toast.severity}
              variant="filled"
              sx={{
                borderRadius: 3,
                py: 2,
                px: 2.5,
                alignItems: "center",
                fontSize: 14,
              }}
            >
              {toast.message}
            </Alert>

            {toast.severity === "error" && (
              <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button onClick={closeToast} variant="contained" color="primary">
                  بستن
                </Button>
                <Button
                  onClick={() => {
                    closeToast();
                    setPassword("");
                  }}
                  variant="outlined"
                  sx={{ bgcolor: "white" }}
                >
                  تلاش مجدد
                </Button>
              </Box>
            )}
          </Box>
        </Backdrop>
      </Box>
    </ThemeProvider>
  );
}
